import { Divider, Button, Input, Modal, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useState, useRef, memo } from "react";
import _ from "lodash";
import { goToBounds, selectAsset, selectIssue, selectObservation, updateIssue, customAudit } from "redux/actions";
import OBCSpinner from "components/util/OBC";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faExclamationCircle,
    faCheckCircle,
    faEdit,
    faInfoCircle,
    faRuler,
    faSpellCheck,
    faHashtag,
    faUserLock,
    faTimes,
    faClipboard,
} from "@fortawesome/free-solid-svg-icons";
import { faThermometerEmpty } from "@fortawesome/pro-light-svg-icons";
import Highlighter from "react-highlight-words";
import { convertToTimezone, convertTsToMilliseconds } from "components/util/TimezoneUtils";
import Tippy from "@tippyjs/react";
import { combineGroundTruthConsensus, StatusElement } from "./issueUtils";
import { faMapMarkerAlt } from "@fortawesome/pro-solid-svg-icons";
import { degreesToUserPreference, getTempUnit } from "../util/TemperatureUtils";
import RouteCoordinateSystems from "../util/positionalSystems/RouteCoordinateSystems";
import { getRawApiImage } from "components/RawImageComponent";
import { areEqual, FixedSizeList } from "react-window";
import { temperatureUnitPreferenceSelector } from "redux/selectors";

const issueObservationsSelector = (state) => state.issues.observations;
const assetObservationsSelector = (state) => state.mlAssets.observations;
const userConfigSelector = (state) => state.userDetails.userConfig;
const issueGroupsSelector = (state) => state.issues.groups;
const assetGroupsSelector = (state) => state.mlAssets.groups;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const currentDashboardSelector = (state) => _.find(state.dashboards, (dashboard) => dashboard.access_token === state.access_token);
const loadingObservationsSelector = (state) => state.issues.loadingObservations === state.issues.selected.issue;

const GUTTER_SIZE = 5;

const ObservationItem = ({ observation, setParentSize, showFailedMatches, userConfig }) => {
    const dispatch = useDispatch();
    const currentDashboard = useSelector(currentDashboardSelector);

    const [imageLoading, setImageLoading] = useState(true);
    const [imageLoadingError, setImageLoadingError] = useState(false);
    const [observationImageSrc, setObservationImageSrc] = useState(null);
    const temperatureUnits = userConfig.temperature_units ?? currentDashboard?.config?.default_temperature_unit;

    const onClick = (e) => {
        e.stopPropagation();
        dispatch(selectObservation(observation.id));
    };

    const humanDate = useMemo(() => {
        const newDate = new Date(convertTsToMilliseconds(observation.timestamp));
        return convertToTimezone(newDate, userConfig.convert_to_utc);
    }, [observation.timestamp, userConfig]);

    const infoItems = useMemo(() => {
        const observationInfo = observation.observation_info;
        const infoItems = [];

        if (observationInfo.best_temp) {
            infoItems.push(
                <span
                    className="obsTemp"
                    key="temp">
                    {degreesToUserPreference(observationInfo.best_temp, temperatureUnits)} {getTempUnit(temperatureUnits)}
                </span>,
            );
        }

        if (observationInfo.gap_mm) {
            infoItems.push(<span key="gap">{observationInfo.gap_mm}mm</span>);
        }
        return infoItems;
    }, [observation.observation_info, temperatureUnits]);

    useEffect(() => {
        if (!observationImageSrc) {
            const callback = (imageSrc) => {
                setImageLoading(false);
                if (imageSrc) {
                    setObservationImageSrc(imageSrc);
                }
            };
            getRawApiImage(observation.image_url, currentDashboard.access_token, callback);
        }
    }, []);

    const observationImage = useMemo(() => {
        if (imageLoading) {
            return (
                <OBCSpinner
                    size={50}
                    speed={3}
                    colorScheme={"mono"}
                />
            );
        }

        if (observationImageSrc) {
            return (
                <img
                    isLoading={imageLoading}
                    src={observationImageSrc}
                    onLoad={setParentSize}
                    alt="Observation"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={() => {
                        if (!imageLoadingError) {
                            setImageLoadingError(true);
                            setParentSize();
                        }
                    }}
                />
            );
        }

        return null;
    }, [imageLoading, imageLoadingError, observationImageSrc, setParentSize]);

    return (
        <div class="ObservationItemContainer">
            <div
                className="ObservationItem"
                id="ObservationItem"
                onClick={onClick}>
                <span className="ID">
                    #{observation.id}
                    {observation.matching_state === 1 && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Matched On Consensus"}
                            theme="aivr"
                            zIndex="600"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faSpellCheck}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                    {observation.matching_state === 9 && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Force Matched"}
                            theme="aivr"
                            zIndex="600"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faUserLock}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                    {[88, 89].includes(observation.matching_state) && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Not Matched"}
                            theme="aivr"
                            zIndex="600"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                </span>
                <span className="SessionID">
                    Session:{" "}
                    <span style={{ marginLeft: 5 }}>
                        <b>#{observation.session_id}</b>
                    </span>
                </span>
                <span className="DateAndTime">{humanDate}</span>
                {infoItems}

                <div className="ObservationImageContainer">
                    {!imageLoading &&
                        (imageLoadingError ? (
                            <div class="MissingImagePlaceholder">
                                <span>
                                    Image not <br />
                                    found
                                </span>
                            </div>
                        ) : (
                            <Tippy
                                arrow={true}
                                placement="bottom"
                                content={<div className="IssueImageContainerToolTip">{observationImage}</div>}
                                theme="aivr"
                                zIndex="1000"
                                delay="100">
                                {observationImage}
                            </Tippy>
                        ))}
                </div>
            </div>
        </div>
    );
};

const IssueItem = ({ index, issue, searchQuery, isIssue, autoAck, setSize, windowWidth, selected, minHeight, showFailedMatches, hasMissingImages }) => {
    const dispatch = useDispatch();

    const [notes, setNotes] = useState("");
    const [originalNotes, setOriginalNotes] = useState("");
    const [inputModalOpen, setInputModalOpen] = useState(false);
    const [issueImageSrc, setIssueImageSrc] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageLoadingError, setImageLoadingError] = useState(false);
    const [copyLinkTooltip, setCopyLinkTooltip] = useState("Copy link to issue");

    const issueObservations = useSelector(issueObservationsSelector);
    const assetObservations = useSelector(assetObservationsSelector);
    const userConfig = useSelector(userConfigSelector);
    const issueGroups = useSelector(issueGroupsSelector);
    const assetGroups = useSelector(assetGroupsSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const loadingObservations = useSelector(loadingObservationsSelector);
    const temperatureUnitPreference = useSelector(temperatureUnitPreferenceSelector);

    const ref = useRef();

    const observations = useMemo(() => {
        return isIssue ? issueObservations : assetObservations.observations;
    }, [assetObservations.observations, isIssue, issueObservations]);

    const setIssueSize = () => {
        const size = ref.current.getBoundingClientRect().height;
        setSize(index, size);
    };

    useEffect(() => {
        setIssueSize();
    }, [setSize, index, windowWidth, selected, observations]);

    const humanReadableDate = useMemo(() => {
        if (issue.summary_data && issue.summary_data.last_observed) {
            const timeDate = new Date(convertTsToMilliseconds(issue.summary_data.last_observed));
            return convertToTimezone(timeDate, userConfig.convert_to_utc, null, false);
        } else {
            return null;
        }
    }, [issue.summary_data, userConfig.convert_to_utc]);

    const onIssueItemClick = (evt, openModal = false) => {
        if (
            [
                "StatusContainer",
                "StatusContainer Clickable",
                "StatusContainer ant-popover-open",
                "ant-btn ant-btn-sm",
                "ant-btn ant-btn-primary ant-btn-sm",
            ].includes(evt.target?.className)
        ) {
            return;
        }
        if (selected) {
            if (_.isEmpty(evt)) {
                if (openModal && observations && observations.length) {
                    dispatch(selectObservation(observations[0].id));
                }
            } else {
                dispatch(selectIssue(null));
                dispatch(selectAsset(null));
            }
        } else {
            if (isIssue) {
                if (autoAck && (issue.state === 0 || (issue.state === 1 && issueHasUpdates))) {
                    // acknowledge new issues on selection, and for already acknowledged issues
                    // this will update the time at which they were issued and suppress 'new observations' warnings
                    dispatch(updateIssue(issue.id, { state: 1 }, true));
                }
                dispatch(selectIssue(issue.id));
            } else {
                dispatch(selectAsset(issue.id, showFailedMatches)).then((observations) => {
                    if (openModal && observations && observations.length) {
                        dispatch(selectObservation(observations[0].id));
                    }
                });
            }

            let north = issue["coords"][1] + 0.002;
            let east = issue["coords"][0] + 0.002;
            let south = issue["coords"][1] - 0.002;
            let west = issue["coords"][0] - 0.002;
            dispatch(
                goToBounds({
                    north,
                    south,
                    east,
                    west,
                }),
            );

            const issueType = _.get(issue, "class", "");
            dispatch(
                customAudit(
                    "issue_item_click",
                    { issue_type: issueType, target: "Issue Tab", source: "mouse" },
                    `issue_item_click ${issueType} clicked from the Issues tab`,
                ),
            );
        }
    };

    const orderedObservations = useMemo(() => {
        if (!observations) {
            return [];
        }

        const filteredObservations = observations.filter((obs) => {
            return !obs.classification || obs.classification === "verified";
        });

        return _.orderBy(filteredObservations, "timestamp", "desc");
    }, [observations]);

    const ObservationItemRow = memo(({ index, style }) => {
        const observation = orderedObservations[index];
        return (
            <div style={style}>
                <ObservationItem
                    key={observation.id}
                    setParentSize={setIssueSize}
                    observation={observation}
                    userConfig={userConfig}
                />
            </div>
        );
    }, areEqual);

    const observationItems = useMemo(() => {
        if (loadingObservations) {
            return (
                <div className="SessionSpinner">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }

        if (!selected) {
            return [];
        }

        if (orderedObservations.length === 0) {
            return <p className="NoObservationsText">No Observations Found</p>;
        }

        const totalItems = orderedObservations.length;
        const itemSize = 120 + GUTTER_SIZE;
        const height = Math.min(300, GUTTER_SIZE + itemSize * totalItems);

        return (
            <div
                className="ObservationsList"
                onClick={(e) => e.stopPropagation()}>
                <FixedSizeList
                    itemCount={totalItems}
                    itemSize={itemSize}
                    height={height}
                    width={"100%"}>
                    {ObservationItemRow}
                </FixedSizeList>
            </div>
        );
    }, [orderedObservations, selected, loadingObservations, userConfig]);

    const issueHasUpdates = useMemo(() => {
        // 'updated' if match_ts (the last time this issue was updated with new observations)
        // is greater than 'state change ts' - state_change_ts is updated whenever 'state'
        // is passed in update issue - even if it is the same
        if (issue.state_change_ts && issue.state_change_ts * 1000 < issue.match_ts) {
            return true;
        } else {
            return false;
        }
    }, [issue.match_ts, issue.state_change_ts]);

    const issueStatus = useMemo(() => {
        let colour = "red";
        let icon = faExclamationCircle;
        let text = isIssue ? "New Issue" : "New Asset";

        if (issue.state === 1) {
            colour = "grey";
            text = "Acknowledged";
            icon = faInfoCircle;

            if (issueHasUpdates) {
                colour = "orange";
                text = "New Observations";
                icon = faExclamationCircle;
            }
        } else if (issue.state === 2) {
            colour = "green";
            text = "Closed";
            icon = faCheckCircle;
        }

        return (
            <div className={`StatusDiv`}>
                <FontAwesomeIcon
                    icon={icon}
                    className={`Icon ${colour}`}
                />
                <p className="StatusText">{text}</p>
            </div>
        );
    }, [isIssue, issue.state, issueHasUpdates]);

    const updateStatus = useCallback(
        (e, status) => {
            e.stopPropagation();

            dispatch(updateIssue(issue.id, { state: status }, isIssue));
        },
        [dispatch, issue.id, isIssue],
    );

    const reviewButtons = useMemo(() => {
        const buttons = [];

        if ((issue.state !== 1 || issueHasUpdates) && issue.state !== 2) {
            buttons.push(
                <Button
                    key="ack"
                    onClick={(e) => {
                        updateStatus(e, 1);
                        const issueType = _.get(issue, "class", "");
                        dispatch(
                            customAudit(
                                "issue_acknowledge_click",
                                { issue_type: issueType, target: "Issue Tab", source: "mouse" },
                                `issue_acknowledge_click ${issueType} acknowledged from the Issues tab`,
                            ),
                        );
                    }}
                    className="UpdateButton">
                    Acknowledge
                </Button>,
            );
        }

        if (issue.state !== 2) {
            buttons.push(
                <Button
                    key="update"
                    onClick={(e) => updateStatus(e, 2)}
                    className="UpdateButton">
                    Close
                </Button>,
            );
        }

        if (issue.state === 2) {
            buttons.push(
                <Button
                    key="open"
                    onClick={(e) => updateStatus(e, 0)}
                    className="UpdateButton">
                    Re-Open
                </Button>,
            );
        }

        return buttons;
    }, [issue.state, issueHasUpdates, updateStatus]);

    const updateIssuePriority = (e, priority) => {
        e.stopPropagation();
        let update = { priority };
        if (autoAck && issue.state === 0) {
            update.state = 1;
        }
        dispatch(updateIssue(issue.id, update, isIssue));
    };

    const openInputModal = useCallback(
        (e, type) => {
            e.stopPropagation();
            setInputModalOpen(type);

            let noteValue = "";
            if (type === "notes") {
                noteValue = issue.note || "";
            } else if (type === "external_ref") {
                noteValue = issue.external_reference || "";
            }
            setNotes(noteValue);
            setOriginalNotes(noteValue);
        },
        [issue],
    );

    const saveNotes = useCallback(() => {
        let update = { note: notes };
        if (autoAck && issue.state === 0) {
            update.state = 1;
        }
        dispatch(updateIssue(issue.id, update, isIssue));
        setOriginalNotes(notes);
        setInputModalOpen(false);
    }, [autoAck, dispatch, isIssue, issue, notes]);

    const saveExternalRef = useCallback(() => {
        let update = { external_reference: notes };
        dispatch(updateIssue(issue.id, update, isIssue));
        setOriginalNotes(notes);
        setInputModalOpen(false);
    }, [dispatch, isIssue, issue, notes]);

    const handleCancel = useCallback(() => {
        setInputModalOpen(false);
        setNotes(originalNotes);
    }, [originalNotes]);

    const convertDate = useCallback((date) => {
        const newDate = new Date(convertTsToMilliseconds(date));
        return convertToTimezone(newDate, userConfig.convert_to_utc);
    }, []);

    const openGroundTruthModal = (e) => {
        e.stopPropagation();
        onIssueItemClick({}, true);
    };

    const onCopyLinkClicked = useCallback(
        (e) => {
            e.stopPropagation();
            const hostname = window.location.hostname;
            const port = window.location.port ? `:${window.location.port}` : "";
            const protocol = window.location.protocol;
            const issueLink = `${protocol}//${hostname}${port}/issue/${issue.id}`;

            navigator.clipboard
                .writeText(issueLink)
                .then(() => {
                    setCopyLinkTooltip("Copied");
                })
                .catch(() => {
                    // Fallback for browsers that don't support clipboard API
                    setCopyLinkTooltip("Copy failed");
                });
        },
        [issue.id],
    );

    const observationsWorkspaceWarning = useMemo(() => {
        let content = null;
        if (selected && !loadingObservations) {
            const issueCount = _.get(issue, "summary_data.match_count", 0);
            if (issueCount !== issueObservations.length) {
                content = (
                    <Tippy content="Some observations of this issue are not visible in this workspace">
                        <div style={{ display: "inline" }}>
                            <FontAwesomeIcon
                                icon={faExclamationCircle}
                                style={{ color: "#e55151", fontSize: "16px", cursor: "pointer" }}
                            />
                        </div>
                    </Tippy>
                );
            }
        }
        return content;
    }, [issue, issueObservations, selected, loadingObservations]);

    const greenZoneOverflowExplanation = (
        <Tippy content="'Green zone overflow' refers to how far outside of the safe operating zone the tensioner is sitting, measured in cm">
            <div style={{ display: "inline" }}>
                <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ fontSize: "16px", cursor: "pointer" }}
                />
            </div>
        </Tippy>
    );

    const infoFields = useMemo(() => {
        const fields = issue.summary_data.fields || {};

        const infoElements = [];

        let match_count = _.get(issue, ["summary_data", "match_count"], false);
        if (match_count) {
            infoElements.push(
                <div
                    id="observation-count-display"
                    className="IssueInfo"
                    key="match_info">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faHashtag}
                    />
                    <p className="IssueInfoText">
                        <b>Observations</b>: {match_count} (Last seen {humanReadableDate}) {observationsWorkspaceWarning}
                    </p>
                </div>,
            );
        } else {
            infoElements.push(
                <div
                    id="observation-count-display"
                    className="IssueInfo"
                    key="match_info">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faHashtag}
                    />
                    <p className="IssueInfoText">
                        <b>Last Observation</b>: {humanReadableDate}
                    </p>
                </div>,
            );
        }

        if (fields.last_temperature) {
            infoElements.push(
                <div
                    id="last-temperature-display"
                    className="IssueInfo"
                    key="temp">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Last Temperature</b>: {degreesToUserPreference(fields.last_temperature, temperatureUnitPreference)}{" "}
                        {getTempUnit(temperatureUnitPreference)}
                    </p>
                </div>,
            );
        }

        if (fields.highest_temperature) {
            infoElements.push(
                <div
                    id="highest-temperature-display"
                    className="IssueInfo"
                    key="max_temp">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Highest Temperature</b>: {degreesToUserPreference(fields.highest_temperature, temperatureUnitPreference)}{" "}
                        {getTempUnit(temperatureUnitPreference)}
                    </p>
                </div>,
            );
        }

        if (fields.last_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Last Gap</b>: {fields.last_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.max_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="max_gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Largest Gap</b>: {fields.max_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.min_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="min_gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Smallest Gap</b>: {fields.min_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.last_height_diff_px) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="last_height_diff_px">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Last Height Difference</b>: {fields.last_height_diff_px}px
                    </p>
                </div>,
            );
        }

        if (fields.bottom_out_temperature) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="severity">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Latest Bottom Out Temperature</b>:{" "}
                        {fields.bottom_out_temperature > 50 ? `50°C+ (${fields.bottom_out_temperature}°C)` : `${fields.bottom_out_temperature}°C`}
                    </p>
                </div>,
            );
        }

        if (fields.severity) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="severity">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Green zone overflow</b>: {fields.severity}cm {greenZoneOverflowExplanation}
                    </p>
                </div>,
            );
        }

        if (issue.elr) {
            let elrObj = null;
            RouteCoordinateSystems.forEach((system) => {
                if (Object.keys(system.handlerClass.display_formats).indexOf(elrUnits) !== -1) {
                    elrObj = system.create(issue.elr, issue.chains, issue.trid);
                }
            });
            if (elrObj !== null) {
                infoElements.push(
                    <div
                        id="route-position-display"
                        className="IssueInfo"
                        key="severity">
                        <FontAwesomeIcon
                            className="InfoIcon"
                            icon={faMapMarkerAlt}
                        />
                        <p className="IssueInfoText">
                            <b>Route Position</b>:{" "}
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={searchQuery.toLowerCase().split(" ")}
                                autoEscape={true}
                                textToHighlight={elrObj.to_string(elrUnits)}
                            />
                        </p>
                    </div>,
                );
            }
        }

        if (!isIssue) {
            let propertyElements = [];

            const propertyData = combineGroundTruthConsensus(issue.ground_truth_data, issue.consensus_data, issue.class);
            propertyData.forEach((property) => {
                propertyElements.push(
                    <tr className="PropertyRow">
                        <td className="PropertyKey">{property.name}</td>
                        <td className="PropertyValue">{property.value}</td>
                        {property.status && (
                            <>
                                <td className="PropertyValue">
                                    <StatusElement
                                        issue={issue}
                                        groundTruthKey={property.fieldName}
                                        status={property.status}
                                    />
                                </td>
                                <td className="PropertyValue">
                                    <FontAwesomeIcon
                                        className={"EditIcon"}
                                        icon={faEdit}
                                        onClick={openGroundTruthModal}
                                    />
                                </td>
                            </>
                        )}
                    </tr>,
                );
            });

            let propertiesContainerElement = (
                <table className="PropertiesContainer">
                    <tbody>{propertyElements}</tbody>
                </table>
            );

            infoElements.push(propertiesContainerElement);
        }

        return infoElements;
    }, [issue, isIssue, humanReadableDate, observationsWorkspaceWarning]);

    useEffect(() => {
        if (!issueImageSrc) {
            const callback = (imageSrc) => {
                setImageLoading(false);
                if (imageSrc) {
                    setIssueImageSrc(imageSrc);
                }
            };

            const url = _.get(issue.summary_data, ["images", "thumbnail"]);
            getRawApiImage(url, currentDashboard.access_token, callback);
        }
    }, [currentDashboard.access_token, issueImageSrc, issue.summary_data]);

    const image = useMemo(() => {
        if (imageLoading) {
            return (
                <OBCSpinner
                    size={50}
                    speed={3}
                    colorScheme={"mono"}
                />
            );
        }

        if (issueImageSrc) {
            return (
                <img
                    src={issueImageSrc}
                    alt="Issue"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={() => {
                        if (!hasMissingImages.has(issue.id)) {
                            setImageLoadingError(true);
                            hasMissingImages.add(issue.id);
                        }
                    }}
                />
            );
        }

        return null;
    }, [hasMissingImages, imageLoading, issue.id, issueImageSrc]);

    const groupsToUse = useMemo(() => {
        return isIssue ? issueGroups : assetGroups;
    }, [assetGroups, isIssue, issueGroups]);

    const customInputElement = useCallback(
        (note = true) => {
            let coreContent = null;

            let source, changeTs, content, label, inputType;

            if (note) {
                source = issue.note_source;
                changeTs = issue.note_change_ts;
                content = issue.note;
                label = "Note";
                inputType = "notes";
            } else {
                source = issue.external_reference_source;
                content = issue.external_reference;
                inputType = "external_ref";

                label = _.get(groupsToUse, [issue.issue_group_id, "external_ref_name"]);
                if (!label) {
                    label = "External ID";
                }

                if (content) {
                    content = `${label}: ${content}`;
                }
            }

            if (!content) {
                if (source) {
                    coreContent = (
                        <>
                            <span className="IssueRemoved">[Removed]</span>
                            <Tooltip
                                placement="bottomLeft"
                                mouseLeaveDelay={0}
                                title={
                                    <span>
                                        {label} removed by {source} <br />
                                        {!!changeTs && `Date: ${convertDate(changeTs)}`}
                                    </span>
                                }>
                                <span className="IssueAuthorAndDate">
                                    {source} - {!!changeTs && `${moment.duration(moment().diff(moment(changeTs * 1000))).humanize()} ago`}
                                </span>
                            </Tooltip>
                        </>
                    );
                } else {
                    coreContent = (
                        <>
                            <p className="IssueNoteText Empty">Enter {label}</p>
                        </>
                    );
                }
            } else {
                coreContent = (
                    <div className="IssueNoteText">
                        <Tooltip
                            overlayClassName="IssueNoteTextTooltip"
                            placement="bottomLeft"
                            mouseLeaveDelay={0}
                            title={content}>
                            <div className={searchQuery.length ? "" : "IssueNoteTextCollapsed"}>
                                <Highlighter
                                    highlightClassName="ObcTextHighlight"
                                    searchWords={searchQuery.split(" ")}
                                    autoEscape={true}
                                    textToHighlight={content}
                                />
                            </div>
                        </Tooltip>
                        {source && (
                            <Tooltip
                                placement="bottomLeft"
                                mouseLeaveDelay={0}
                                title={
                                    <span>
                                        Last edited by {source} <br />
                                        {!!changeTs && <br /> && `Date: ${convertDate(changeTs)}`}
                                    </span>
                                }>
                                <div className="IssueAuthorAndDate">
                                    {source} {!!changeTs && ` - ${moment.duration(moment().diff(moment(changeTs * 1000))).humanize()} ago`}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                );
            }

            return (
                <div
                    id={note ? "enter-note-field" : "enter-external-ref-field"}
                    className="IssueInfo Note">
                    {coreContent}
                    <FontAwesomeIcon
                        className={"InfoIcon Edit" + (content ? "" : " Empty")}
                        icon={faEdit}
                        onClick={(e) => openInputModal(e, inputType)}
                    />
                </div>
            );
        },
        [convertDate, groupsToUse, issue, openInputModal, searchQuery],
    );

    const inputModal = useMemo(() => {
        if (!inputModalOpen) {
            return null;
        }

        let label, onSave;

        if (inputModalOpen === "notes") {
            label = "notes";
            onSave = () => {
                saveNotes();
            };
        } else if (inputModalOpen === "external_ref") {
            onSave = () => {
                saveExternalRef();
            };
            label = _.get(groupsToUse, [issue.issue_group_id, "external_ref_name"]);
            if (!label) {
                label = "External ID";
            }
        }

        return (
            <Modal
                key={`notes-modal-${issue.id}`}
                title={`Edit ${label}`}
                onCancel={handleCancel}
                onOk={onSave}
                footer={[
                    <Button
                        key="cancel"
                        type="default"
                        onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={onSave}>
                        Save
                    </Button>,
                ]}
                visible={true}>
                <div className="IssueNotesContainer">
                    <Input.TextArea
                        autoFocus
                        placeholder={`Enter ${label}`}
                        rows={10}
                        className="NotesInput"
                        value={notes}
                        onPressEnter={onSave}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>
            </Modal>
        );
    }, [groupsToUse, handleCancel, inputModalOpen, issue, notes, saveExternalRef, saveNotes]);

    return (
        <div className="IssueListItemContainer">
            {inputModal}
            <div
                className={"IssueListItem" + (selected ? " Open" : " Closed")}
                id={"LoadedIssueItem"}
                onClick={onIssueItemClick}
                style={{ minHeight }}
                ref={ref}>
                <div className="IssueHeader">
                    <div className="IssueLeftContainer">
                        <div className="IssueInfo">
                            <p className="IssueType">{_.get(groupsToUse, [issue.issue_group_id, "display_name"], issue.class) || issue.class}</p>
                            {issueStatus}
                        </div>

                        {customInputElement(true)}
                        {customInputElement(false)}

                        {infoFields}

                        {isIssue && (
                            <div
                                className="IssueInfo"
                                style={{ marginTop: 10, flexDirection: "column" }}>
                                <p style={{ marginBottom: 3 }}>
                                    <strong>Priority</strong>
                                </p>
                                <Button.Group>
                                    <Button
                                        id="priority-low-button"
                                        value="low"
                                        className={"RadioButton" + (issue.priority === 1 ? " Active" : "")}
                                        onClick={(e) => updateIssuePriority(e, 1)}>
                                        Low
                                    </Button>
                                    <Button
                                        id="priority-medium-button"
                                        value="medium"
                                        className={"RadioButton" + (issue.priority === 2 ? " Active" : "")}
                                        onClick={(e) => updateIssuePriority(e, 2)}>
                                        Medium
                                    </Button>
                                    <Button
                                        id="priority-high-button"
                                        value="high"
                                        className={"RadioButton" + (issue.priority === 3 ? " Active" : "")}
                                        onClick={(e) => updateIssuePriority(e, 3)}>
                                        High
                                    </Button>
                                </Button.Group>
                            </div>
                        )}

                        {isIssue && (
                            <div
                                className="IssueInfo"
                                style={{ marginTop: 10 }}>
                                {reviewButtons}
                            </div>
                        )}
                    </div>

                    <div className="IssueRightContainer">
                        <div className="IssueRightContainerTop">
                            <Tooltip
                                title={copyLinkTooltip}
                                onVisibleChange={(visible) => {
                                    if (!visible) {
                                        setCopyLinkTooltip("Copy link to issue");
                                    }
                                }}>
                                <div
                                    className="CopyLinkButton"
                                    onClick={onCopyLinkClicked}
                                    aria-label="Copy link to issue">
                                    <FontAwesomeIcon
                                        icon={faClipboard}
                                        className="CopyLinkIcon"
                                    />
                                </div>
                            </Tooltip>
                            <div className="IssueIdContainer">
                                {issue.recalculating && (
                                    <Tooltip
                                        title={
                                            <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                                                <span>Recalculating {isIssue ? "Issue" : "Asset"}</span>
                                                <span>Some data may be out of date</span>
                                            </div>
                                        }>
                                        <div>
                                            <OBCSpinner
                                                size={20}
                                                color={"#e8dfff"}
                                                speed={3}
                                            />
                                        </div>
                                    </Tooltip>
                                )}

                                <div className="IssueID">
                                    <span>
                                        #
                                        <Highlighter
                                            highlightClassName="ObcTextHighlight"
                                            searchWords={searchQuery.split(" ")}
                                            autoEscape={true}
                                            textToHighlight={String(issue.id)}
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="IssueImageContainer">
                            {hasMissingImages.has(issue.id) || imageLoadingError ? (
                                <div class="MissingImagePlaceholder">
                                    <span>Image not found</span>
                                </div>
                            ) : (
                                <Tippy
                                    arrow={true}
                                    placement="bottom"
                                    content={<div className="IssueImageContainerToolTip">{image}</div>}
                                    theme="aivr"
                                    zIndex="1000"
                                    delay="300">
                                    {image}
                                </Tippy>
                            )}
                        </div>
                    </div>
                </div>
                <div className="IssueObservations">
                    <Divider className="Divider">Observations</Divider>
                    {observationItems}
                </div>
            </div>
        </div>
    );
};

export default IssueItem;
